import { useRefreshTrigger } from "@/composables/refreshTrigger";
import { progressEvent, useProgressEvents } from "@/composables/useProgressEvents";
import { $t } from "@/i18n";
import { router, Routes } from "@/router";
import { useAsyncOperationProgress } from "@/store/asyncOperationProgress";
import { useWorkspace } from "@/store/workspace";
import { toPercentage } from "@/utils/progressPercentage";
import { showToastForError, showToastForSuccess } from "@/utils/toast";

const EVENT_CODE_PROGRESS = "tenant_copy.progress";
const EVENT_CODE_FINISHED = "tenant_copy.finished";

interface TenantCopyProgressPayload {
  sourceTenantId: string;
  destTenantId: string;
  completed: number;
  total: number;
}

interface TenantCopyFinishedPayload extends TenantCopyProgressPayload {
  error?: string;
}

function isProgressPayload(data: unknown): data is TenantCopyProgressPayload {
  return (
    !!data &&
    typeof data === "object" &&
    typeof (data as TenantCopyProgressPayload).destTenantId === "string" &&
    typeof (data as TenantCopyProgressPayload).completed === "number" &&
    typeof (data as TenantCopyProgressPayload).total === "number"
  );
}

function isFinishedPayload(data: unknown): data is TenantCopyFinishedPayload {
  return isProgressPayload(data);
}

/**
 * Feeds asyncOperationProgress's tenant-copy operations from the SSE stream (#3490), matched by
 * destTenantId — TenantDetailsCopy.vue uses the new tenant's own id as the operation id, so no
 * separate correlation table is needed.
 */
export default function useTenantCopyProgressEvents() {
  const progress = useAsyncOperationProgress();
  const workspace = useWorkspace();
  const refetchList = useRefreshTrigger();

  function handleFinished(data: TenantCopyFinishedPayload) {
    refetchList();
    void workspace.tenantsRefetch();

    if (data.error) {
      progress.remove(data.destTenantId);
      showToastForError($t("tenantManagement.copy.label.copyFailedToast"));
      return;
    }

    progress.finish(data.destTenantId);
    showToastForSuccess($t("tenantManagement.copy.label.copySuccessToast"));

    const currentRoute = router.currentRoute.value;
    if (
      currentRoute.name === Routes.TENANT_MANAGEMENT_DETAILS_COPY &&
      currentRoute.params.tenantId === data.sourceTenantId
    ) {
      void router.push({ name: Routes.TENANT_MANAGEMENT_DETAILS, params: { tenantId: data.destTenantId } });
    }
  }

  return useProgressEvents([
    progressEvent(EVENT_CODE_PROGRESS, isProgressPayload, (data) => {
      progress.update(data.destTenantId, { percentage: toPercentage(data.completed, data.total) });
    }),
    progressEvent(EVENT_CODE_FINISHED, isFinishedPayload, handleFinished),
  ]);
}
